// canvas/cards.js: pixel-art card renderer (cream restyle, step 1).
//
// renderCard draws a single card to a 2D canvas. No state, no events,
// pure pixels. Callers (hand.js, trick-area.js and upcard.js, plus the
// HeroAnimation preview) own positioning + interaction.
//
// Card IDs are 2-char strings: rank + suit. Ranks: 9 T J Q K A. Suits:
// S H D C. Per cards.lua header comment, "T" is ten so M5 art-asset
// filenames match. Joker IDs are deliberately not supported (no jokers
// in MVP, see plan-m2.md ## Out of Scope).
//
// The look copies the chosen cream mockup
// (docs/design-2026-10/restyle-cream/): a #fcf5e3 face with a dark
// outline and rounded corners, a solid dark edge straight below, one
// top-left corner index (rank then suit), pips / one ace suit / a framed
// portrait in the middle, and a thin gold frame inside the outline on
// trump cards. Art data and per-size geometry live in pixel-cards.js.
//
// Crisp-pixel rule: every art cell (suits, portraits, rank glyphs, back
// pattern) is painted with fillRect on whole device pixels. The game
// canvases keep a backing store of CSS size x devicePixelRatio with the
// context scaled by the ratio (see hidpi.js), so callers still pass CSS
// px. Cell sizes and positions come from cardGeometry (whole CSS px) and
// the card origin is rounded, so at DPR 1, 2 and 3 every cell is exactly
// 1, 2 or 3 device pixels per CSS px. renderCard also reads the context
// transform and snaps each rect's edges to the device grid (snapRect),
// which keeps cells aligned at a fractional ratio (1.25, 1.5) and under
// the translate/scale of the up-card intro and trick sweeps. At a
// fractional ratio neighbouring cells can differ by one device pixel;
// alignment holds, uniformity does not. That is the documented cost of
// fractional DPR. Only the outline, the edge and the trump frame use
// paths (their round corners are anti-aliased, as the browser draws the
// mockup's borders).
//
// Rank corners are Tiny5 glyph maps (see RANK_GLYPHS in pixel-cards.js
// for how they were made), not canvas text: Chrome blurs pixel fonts
// drawn with fillText.

import {
  SUIT_MAPS,
  SUIT_INK,
  INK,
  FACE,
  GOLD,
  BACK,
  PORTRAIT_GROUND,
  PORTRAIT_LINE,
  PORTRAITS,
  PORTRAIT_COLS,
  PORTRAIT_ROWS,
  PORTRAIT_COLOURS,
  LAYOUTS,
  RANK_GLYPHS,
  RANK_ADVANCE,
  RANK_INK_TOP,
  cardGeometry,
} from './pixel-cards.js';

const COLOR_CARD_BODY    = FACE;
const COLOR_CARD_BORDER  = INK;
// Solid edge below the card (the mockup's 0 4px 0 box-shadow).
const COLOR_CARD_EDGE    = INK;
// Face-down field. The brick back also keeps the #14/T5 contrast
// contracts against the felt.
const COLOR_CARD_BACK    = BACK.field;

// Suit inks (canonical per-suit map lives in pixel-cards.js).
// COLOR_SUIT_RED/DARK stay as aliases (H/S inks) so older importers keep
// resolving; new code should read SUIT_INK.
const COLOR_SUIT_RED  = SUIT_INK.H;
const COLOR_SUIT_DARK = SUIT_INK.S;

const COLOR_BOWER         = INK;
const COLOR_BOWER_GOLD    = GOLD;   // trump frame + portrait crowns
const COLOR_BOWER_HALO    = GOLD;   // kept: badge-glow alias, same gold
const COLOR_TRUMP_FRAME   = GOLD;
const COLOR_GRAYED     = 'rgba(20, 16, 28, 0.5)';
const COLOR_HOVER_RING = '#F0C040';

const SUIT_SYMBOL = { S: '♠', H: '♥', D: '♦', C: '♣' };
const SUIT_NAME   = { S: 'Spades', H: 'Hearts', D: 'Diamonds', C: 'Clubs' };
const SAME_COLOR  = { S: 'C', C: 'S', H: 'D', D: 'H' };

/**
 * Returns 'right' for a J of trump suit, 'left' for a J of the matching
 * same-color suit, null otherwise. JS port of cards.lua is_bower.
 *
 * @param {string} cardId
 * @param {string|null|undefined} trumpSuit
 * @returns {'right'|'left'|null}
 */
export function isBower(cardId, trumpSuit) {
  if (!trumpSuit || !cardId || cardId.length !== 2) return null;
  if (cardId[0] !== 'J') return null;
  const suit = cardId[1];
  if (suit === trumpSuit) return 'right';
  if (SAME_COLOR[suit] === trumpSuit) return 'left';
  return null;
}

const RANK_NAME = { 9: 'Nine', T: 'Ten', J: 'Jack', Q: 'Queen', K: 'King', A: 'Ace' };

/**
 * Human name for a card id, "Jack of Spades". T7: feeds the hit-zone
 * aria-labels so screen-reader users hear cards, not button soup.
 *
 * @param {string|null|undefined} cardId
 * @returns {string}
 */
export function describeCard(cardId) {
  if (!cardId || cardId.length !== 2) return 'Unknown card';
  const rank = RANK_NAME[cardId[0]];
  const suit = SUIT_NAME[cardId[1]];
  if (!rank || !suit) return 'Unknown card';
  return `${rank} of ${suit}`;
}

/**
 * @param {string} cardId
 * @returns {string|null}
 */
function suitOf(cardId) {
  if (!cardId || cardId.length !== 2) return null;
  return cardId[1];
}

/**
 * @param {string} cardId
 */
function rankOf(cardId) {
  if (!cardId || cardId.length !== 2) return null;
  return cardId[0];
}


/**
 * True for every card of the trump suit plus the left bower (the jack of
 * the same-colour suit). These cards get the gold trump frame.
 *
 * @param {string|null|undefined} cardId
 * @param {string|null|undefined} trumpSuit
 * @returns {boolean}
 */
export function isTrumpCard(cardId, trumpSuit) {
  if (!trumpSuit || !cardId || cardId.length !== 2) return false;
  if (cardId[1] === trumpSuit) return true;
  return isBower(cardId, trumpSuit) === 'left';
}

function pipColor(suit) {
  return SUIT_INK[suit] || SUIT_INK.S;
}

function roundRectPath(ctx, x, y, w, h, r) {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y,     x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x,     y + h, rr);
  ctx.arcTo(x,     y + h, x,     y,     rr);
  ctx.arcTo(x,     y,     x + w, y,     rr);
  ctx.closePath();
}

function fillRoundRect(ctx, x, y, w, h, r) {
  roundRectPath(ctx, x, y, w, h, r);
  ctx.fill();
}

function strokeRoundRect(ctx, x, y, w, h, r) {
  roundRectPath(ctx, x, y, w, h, r);
  ctx.stroke();
}

// Rounded-rect subpath without beginPath, for two-edge (evenodd) fills.
function addRoundRect(ctx, x, y, w, h, r) {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y,     x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x,     y + h, rr);
  ctx.arcTo(x,     y + h, x,     y,     rr);
  ctx.arcTo(x,     y,     x + w, y,     rr);
  ctx.closePath();
}

/**
 * Device-grid painter for one card. Reads the context transform: with an
 * axis-aligned scale (the DPR transform, maybe with the up-card intro's
 * translate and scale on top) it moves each rect edge to the nearest
 * device pixel, so art never straddles two device pixels. A rotated
 * transform cannot be snapped and draws as given. A context without
 * getTransform (the Node test fakes) has no transform to read, so it
 * also draws as given: there CSS px are device px.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @returns {(x:number, y:number, w:number, h:number) => void}
 */
function makeRectPainter(ctx) {
  const m = typeof ctx.getTransform === 'function' ? ctx.getTransform() : null;
  const plain = (x, y, w, h) => ctx.fillRect(x, y, w, h);
  if (!m) return plain;
  if (m.b !== 0 || m.c !== 0 || !(m.a > 0) || !(m.d > 0)) return plain;
  if (m.a === 1 && m.d === 1 && Number.isInteger(m.e) && Number.isInteger(m.f)) return plain;
  const { a, d, e, f } = m;
  const sx = (v) => (Math.round(v * a + e) - e) / a;
  const sy = (v) => (Math.round(v * d + f) - f) / d;
  return (x, y, w, h) => {
    const x0 = sx(x);
    const y0 = sy(y);
    const x1 = sx(x + w);
    const y1 = sy(y + h);
    // A cell thinner than one device pixel still paints one pixel.
    ctx.fillRect(x0, y0, Math.max(x1 - x0, 1 / a), Math.max(y1 - y0, 1 / d));
  };
}

// Paint one map with whole-pixel fillRects. `palette` maps characters to
// fill colours; '.' is always transparent.
function drawMap(ctx, paint, map, ox, oy, cell, palette) {
  let current = null;
  for (let r = 0; r < map.length; r += 1) {
    const row = map[r];
    for (let c = 0; c < row.length; c += 1) {
      const ch = row[c];
      if (ch === '.') continue;
      const color = palette[ch];
      if (!color) throw new Error(`drawMap: no colour for map cell '${ch}'`);
      if (color !== current) {
        ctx.fillStyle = color;
        current = color;
      }
      paint(ox + c * cell, oy + r * cell, cell, cell);
    }
  }
}

function drawSuit(ctx, paint, suit, ox, oy, cell) {
  drawMap(ctx, paint, SUIT_MAPS[suit], ox, oy, cell, { X: pipColor(suit) });
}

// Face-down back, copied from the mockup's backSvg('cream'): inside the
// outline, a brick field with 3x3 darker checks every 6 px (odd rows
// shifted 3 px), a 2 px cream frame 1 px in, and a pixel sun in the
// middle. The mockup clips the frame to the rounded face; here the
// straight frame runs are fillRects and the four corners are painted
// pixel by pixel, keeping only frame pixels whose centre lies inside the
// rounded face (no clip(), so plain test contexts can draw it too).
function drawBack(ctx, paint, x, y, w, h, b, rIn) {
  const iw = w - 2 * b;
  const ih = h - 2 * b;
  const ox = x + b;
  const oy = y + b;
  ctx.fillStyle = BACK.field;
  fillRoundRect(ctx, ox, oy, iw, ih, rIn);
  ctx.fillStyle = BACK.check;
  for (let cy = 6; cy < ih - 6; cy += 6) {
    for (let cx = 6 + ((cy / 6) % 2 ? 3 : 0); cx < iw - 6; cx += 6) {
      paint(ox + cx, oy + cy, 3, 3);
    }
  }
  ctx.fillStyle = BACK.frame;
  // SVG rect x=2 y=2 (iw-4)x(ih-4) stroked 2 px wide covers 1..3 px in.
  const k = Math.min(rIn, Math.floor(iw / 2), Math.floor(ih / 2));
  paint(ox + k, oy + 1, iw - 2 * k, 2);
  paint(ox + k, oy + ih - 3, iw - 2 * k, 2);
  paint(ox + 1, oy + k, 2, ih - 2 * k);
  paint(ox + iw - 3, oy + k, 2, ih - 2 * k);
  for (let j = 1; j < k; j += 1) {
    for (let i = 1; i < k; i += 1) {
      if (i >= 3 && j >= 3) continue;
      const dx = k - (i + 0.5);
      const dy = k - (j + 0.5);
      if (dx * dx + dy * dy > k * k) continue;
      paint(ox + i, oy + j, 1, 1);
      paint(ox + iw - 1 - i, oy + j, 1, 1);
      paint(ox + i, oy + ih - 1 - j, 1, 1);
      paint(ox + iw - 1 - i, oy + ih - 1 - j, 1, 1);
    }
  }
  const sx = ox + Math.round(iw / 2);
  const sy = oy + Math.round(ih / 2);
  const r = Math.max(3, Math.round(Math.min(iw, ih) / 6));
  paint(sx - r, sy - 1, 2 * r, 2);
  paint(sx - 1, sy - r, 2, 2 * r);
  paint(sx - r + 2, sy - r + 2, 2 * r - 4, 2 * r - 4);
}

/**
 * Top-left rank glyph rect (approximate painted box), kept for the #bug1
 * no-overlap contract with bowerGlyphRect. Pure.
 *
 * @returns {{x0:number, y0:number, x1:number, y1:number}}
 */
function rankGlyphRect(x, y, h) {
  const rankSize = Math.max(10, Math.round(h * 0.18));
  const gx = x + 5;
  const gy = y + 4;
  return { x0: gx, y0: gy, x1: gx + rankSize, y1: gy + rankSize };
}

/**
 * Bottom-left bower mark slot, kept as a pure export so the #bug1
 * no-overlap contract still holds. The cream cards draw no crown there:
 * like the mockup, every trump card (both bowers included) is marked by
 * the gold frame inside the outline, so the left bower reads as trump.
 *
 * @returns {{x0:number, y0:number, x1:number, y1:number, size:number}}
 */
function bowerGlyphRect(x, y, h) {
  const size = Math.max(10, Math.round(h * 0.14));
  const gx = x + 4;
  const gy = y + h - 4 - size;
  return { x0: gx, y0: gy, x1: gx + size, y1: gy + size, size };
}

// Corner index: the Tiny5 rank glyph, then the suit beside it (full
// cards) or below it (small cards). Positions come from cardGeometry.
function drawCorner(ctx, paint, rank, suit, x, y, g) {
  const glyph = RANK_GLYPHS[rank];
  const ink = pipColor(suit);
  const rx = x + g.cornerX;
  const ry = y + g.cornerY + RANK_INK_TOP * g.fc;
  drawMap(ctx, paint, glyph, rx, ry, g.fc, { '#': ink });
  if (g.small) {
    drawSuit(ctx, paint, suit, x + g.cornerX, y + g.cornerY + g.suitTop, g.sc);
  } else {
    drawSuit(ctx, paint, suit, rx + RANK_ADVANCE[rank] * g.fc + g.gap, y + g.cornerY + g.suitTop, g.sc);
  }
}

// Centre art for full cards: the pip layout (9, 10), one suit at the ace
// scale (A), or the framed portrait (J, Q, K). Same formulas as the
// mockup's face().
function drawCentreArt(ctx, paint, rank, suit, x, y, g) {
  const layout = LAYOUTS[rank === 'T' ? 10 : rank];
  if (layout) {
    for (const [c, f] of layout) {
      drawSuit(ctx, paint, suit, x + g.cols[c], y + g.top + Math.round((g.bottom - g.top) * f), g.pip);
    }
    return;
  }
  if (rank === 'A') {
    const n = 9 * g.ace;
    const ax = Math.round((g.w - n) / 2);
    const ay = Math.round(g.top + (g.bottom + 9 * g.pip - g.top - n) / 2);
    drawSuit(ctx, paint, suit, x + ax, y + ay, g.ace);
    return;
  }
  const portrait = PORTRAITS[rank];
  if (!portrait) return;
  const [fx, fy, fw, fh] = g.frame;
  ctx.fillStyle = PORTRAIT_LINE;
  paint(x + fx, y + fy, fw, fh);
  ctx.fillStyle = PORTRAIT_GROUND;
  paint(x + fx + 2, y + fy + 2, fw - 4, fh - 4);
  const pp = g.portraitPx;
  const pw = PORTRAIT_COLS * pp;
  const ph = PORTRAIT_ROWS * pp;
  const px = x + fx + Math.round((fw - pw) / 2);
  const py = y + fy + fh - ph - 2;
  drawMap(ctx, paint, portrait, px, py, pp, { ...PORTRAIT_COLOURS, c: pipColor(suit) });
}

/**
 * Render a single card.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} cardId, e.g. "JS", "TH", "9C". Ignored when face_up=false.
 * @param {number} x
 * @param {number} y
 * @param {number} w
 * @param {number} h
 * @param {{
 *   face_up?: boolean,
 *   grayed?: boolean,
 *   hover?: boolean,
 *   bower?: 'right'|'left'|null,
 *   trump?: boolean,
 *   focused?: boolean,
 * }} [opts] trump (or a bower mark) draws the gold trump frame.
 */
export function renderCard(ctx, cardId, x, y, w, h, opts = {}) {
  const faceUp  = opts.face_up !== false;
  const grayed  = !!opts.grayed;
  const hover   = !!opts.hover;
  const focused = !!opts.focused;
  const trump   = faceUp && (opts.trump === true || !!opts.bower);

  // No drawn bitmaps in this renderer (all art is fillRect), but callers
  // may share the context, so pin smoothing off for crisp art.
  ctx.imageSmoothingEnabled = false;
  const paint = makeRectPainter(ctx);

  // Snap the card box to whole backing pixels; every geometry value is a
  // whole number, so card art lands on whole device pixels at DPR 1, 2
  // and 3 (see the module note on fractional ratios).
  const ix = Math.round(x);
  const iy = Math.round(y);
  const iw = Math.round(w);
  const ih = Math.round(h);
  const g = cardGeometry(iw, ih);
  const rIn = Math.max(1, g.r - g.b);

  // 1. Solid edge straight below the card, same rounded shape shifted
  // down. The centre fillRect (full card height) is the per-card probe
  // the hand-hover tests read the card top from (rect y - edge).
  ctx.fillStyle = COLOR_CARD_EDGE;
  fillRoundRect(ctx, ix, iy + g.edge, iw, ih, g.r);
  paint(ix + g.r, iy + g.edge, iw - 2 * g.r, ih);

  // 2. Outline, then the face (or the back) inside it.
  ctx.fillStyle = COLOR_CARD_BORDER;
  fillRoundRect(ctx, ix, iy, iw, ih, g.r);
  if (faceUp) {
    ctx.fillStyle = COLOR_CARD_BODY;
    fillRoundRect(ctx, ix + g.b, iy + g.b, iw - 2 * g.b, ih - 2 * g.b, rIn);
  } else {
    drawBack(ctx, paint, ix, iy, iw, ih, g.b, rIn);
  }

  // 3. Face content. BOTH shape AND colour encode suit (OQ-7 a11y,
  // never colour alone).
  if (faceUp) {
    const suit = suitOf(cardId);
    const rank = rankOf(cardId);
    if (suit && rank && SUIT_MAPS[suit] && RANK_GLYPHS[rank]) {
      drawCorner(ctx, paint, rank, suit, ix, iy, g);
      if (!g.small) drawCentreArt(ctx, paint, rank, suit, ix, iy, g);
    }
  }

  // 4. Grayed treatment (illegal cards in trick_play). T6 strengthened:
  // desaturate the face via a 'saturation' blend (red pips lose their
  // pull) and THEN dim, at rest the legal/illegal split is obvious
  // without hovering. Blend modes are clipped to the card's rounded
  // path, so the felt around the corners is untouched.
  if (grayed) {
    ctx.save();
    ctx.globalCompositeOperation = 'saturation';
    ctx.fillStyle = '#808080';
    fillRoundRect(ctx, ix, iy, iw, ih, g.r);
    ctx.restore();
    ctx.fillStyle = COLOR_GRAYED;
    fillRoundRect(ctx, ix, iy, iw, ih, g.r);
  }

  // 5. Trump: the mockup's gold frame just inside the outline (2 px,
  // 1 px on small cards), on every trump card including the left bower.
  // Drawn after the dim layer, so a trump card you cannot play still
  // shows its frame.
  // The mockup draws it as .card.trump::after: a box over the face with a
  // t px gold border and a corner radius of --card-r-in (6 px), clipped
  // to the face. So the gold is the ring between the face edge (radius
  // rIn) and the frame's inner edge (radius frameR - t). On full cards
  // frameR equals rIn (6 px at the mockup sizes). On small cards the face
  // is only 4 px round (6 px card, 2 px border) while the frame keeps
  // its 6 px corner, so the inner edge is rounder than the face corner.
  if (trump) {
    const t = g.trumpW;
    const frameR = g.small ? g.r : rIn;
    ctx.save();
    ctx.fillStyle = COLOR_TRUMP_FRAME;
    ctx.beginPath();
    addRoundRect(ctx, ix + g.b, iy + g.b, iw - 2 * g.b, ih - 2 * g.b, rIn);
    addRoundRect(ctx, ix + g.b + t, iy + g.b + t, iw - 2 * (g.b + t), ih - 2 * (g.b + t), Math.max(0, frameR - t));
    ctx.fill('evenodd');
    ctx.restore();
  }

  // 6. Hover/focus ring on top of everything.
  if (hover || focused) {
    ctx.save();
    ctx.strokeStyle = COLOR_HOVER_RING;
    ctx.lineWidth = focused ? 3 : 2;
    if (focused) {
      ctx.shadowColor = COLOR_HOVER_RING;
      ctx.shadowBlur = 8;
    }
    strokeRoundRect(ctx, ix - 1, iy - 1, iw + 2, ih + 2, g.r + 1);
    ctx.restore();
  }
}

export const _internals = {
  SAME_COLOR,
  SUIT_NAME,
  SUIT_SYMBOL,
  SUIT_INK,
  COLOR_BOWER,
  COLOR_BOWER_HALO,
  COLOR_BOWER_GOLD,
  COLOR_TRUMP_FRAME,
  COLOR_CARD_BODY,
  COLOR_CARD_BACK,
  COLOR_CARD_EDGE,
  COLOR_SUIT_RED,
  COLOR_SUIT_DARK,
  pipColor,
  suitOf,
  rankOf,
  rankGlyphRect,
  bowerGlyphRect,
};
